import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiPlus, FiList, FiMapPin, FiTruck, FiPackage, FiShoppingBag, FiHeart } from 'react-icons/fi';
import { UserContext } from '../../context/UserContext';
import { useRequireLogin } from '../../utils/requireLogin';
import { apiFetch, parseApiResponse, readApiError, invalidateApiCache } from '../../utils/apiClient';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { showCoinAwardToast } from '../../utils/collectorRewardsUtils';
import { useNotifications } from '../../components/NotificationSystem/NotificationSystem';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import MarkAsSoldDialog from '../../components/MarkAsSoldDialog/MarkAsSoldDialog';
import RemovedListingOptionsDialog from '../../components/RemovedListingOptionsDialog/RemovedListingOptionsDialog';
import BrowseSidebarSticky from '../../components/BrowseSidebarSticky/BrowseSidebarSticky';
import MarketplaceHubNav from '../../components/MarketplaceHubNav/MarketplaceHubNav';
import '../../components/MsvBrowseShell/MsvBrowseShell.css';
import '../Marketplace/Marketplace.css';
import './MyMarketplaceListings.css';

const formatPrice = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '$0';
    if (num % 1 === 0) return `$${num.toLocaleString('en-US')}`;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const timeAgo = (unix) => {
    if (!unix) return '';
    const seconds = Math.max(0, Math.floor(Date.now() / 1000) - Number(unix));
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(unix * 1000).toLocaleDateString();
};

const statusBadgeLabel = (status) => {
    if (status === 'UnderReview') return 'Under Review';
    if (status === 'Rejected') return 'Rejected';
    if (status === 'Removed') return 'Paused';
    return status || 'Active';
};

const MyMarketplaceListings = () => {
    const { user } = useContext(UserContext);
    const { authReady, isAuthenticated } = useRequireLogin('Please log in to manage your marketplace listings.');
    const { success, error: notifyError } = useNotifications();
    const navigate = useNavigate();
    const location = useLocation();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showExpired, setShowExpired] = useState(true);
    const [bannerMessage, setBannerMessage] = useState('');
    const [updatingId, setUpdatingId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [removedListingOptions, setRemovedListingOptions] = useState(null);
    const [removedListingBusyAction, setRemovedListingBusyAction] = useState(null);
    const [pendingRelist, setPendingRelist] = useState(null);
    const [pendingPublish, setPendingPublish] = useState(null);
    const [pendingMoveToCollection, setPendingMoveToCollection] = useState(null);
    const [movingToCollection, setMovingToCollection] = useState(false);
    const [pendingMarkSold, setPendingMarkSold] = useState(null);
    const [deletingListing, setDeletingListing] = useState(false);
    const [relistingListing, setRelistingListing] = useState(false);
    const [publishingListing, setPublishingListing] = useState(false);
    const [markingSold, setMarkingSold] = useState(false);

    const fetchListings = async () => {
        if (!user) return;
        try {
            const res = await apiFetch('/Marketplace/GetUserMarketplaceListings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: String(user.userId),
                    includeSold: true,
                    includeExpired: showExpired,
                }),
            });
            if (res.ok) setListings(await res.json());
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authReady && isAuthenticated && user) {
            setLoading(true);
            fetchListings();
        }
    }, [authReady, isAuthenticated, user, showExpired]);

    useEffect(() => {
        const message = location.state?.message;
        if (!message) return;

        setBannerMessage(message);
        success(message);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state?.message, location.pathname, navigate, success]);

    const updateStatus = async (marketplaceItemId, listingStatus, renewListing = false, saleDetails = null) => {
        setUpdatingId(marketplaceItemId);
        try {
            const payload = {
                marketplaceItemId,
                userId: String(user.userId),
                listingStatus,
                renewListing,
            };

            if (saleDetails) {
                payload.soldOnPlatform = saleDetails.soldOnPlatform;
                if (saleDetails.soldOnPlatform) {
                    payload.soldToUsername = saleDetails.soldToUsername;
                } else {
                    payload.soldElsewhereDetail = saleDetails.soldElsewhereDetail;
                    if (saleDetails.soldToName) payload.soldToName = saleDetails.soldToName;
                }
            }

            const response = await apiFetch('/Marketplace/UpdateMarketplaceStatus', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await parseApiResponse(response);
            if (!response.ok || result?.success === false) {
                notifyError(readApiError(result, 'Could not update listing.'));
                return false;
            }

            if (result?.success !== false) {
                showCoinAwardToast(success, result?.coinAward);
            }
            invalidateApiCache('/Marketplace/SearchMarketplace');
            await fetchListings();
            return result || true;
        } finally {
            setUpdatingId(null);
        }
    };

    const confirmDeleteListing = async () => {
        if (!pendingDelete) return;
        setDeletingListing(true);
        try {
            const ok = await updateStatus(pendingDelete.marketplaceItemId, 'Removed');
            if (ok) {
                setRemovedListingOptions(pendingDelete);
                setPendingDelete(null);
            }
        } finally {
            setDeletingListing(false);
        }
    };

    const moveRemovedListingToCollection = async (listing) => {
        if (!listing) return false;
        setMovingToCollection(true);
        setUpdatingId(listing.marketplaceItemId);
        try {
            const response = await apiFetch('/Marketplace/MoveRemovedListingToCollection', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    marketplaceItemId: listing.marketplaceItemId,
                    userId: String(user.userId),
                }),
            });
            const result = await parseApiResponse(response);
            if (!response.ok || result?.success === false) {
                notifyError(readApiError(result, 'Could not delete this listing from your collection.'));
                return false;
            }

            success(
                listing.listingStatus === 'UnderReview'
                    ? 'Listing cancelled. Item is still in your collection.'
                    : 'Listing deleted. Item is still in your collection.'
            );
            setRemovedListingOptions(null);
            setPendingMoveToCollection(null);
            invalidateApiCache('/Marketplace/SearchMarketplace');
            await fetchListings();
            return true;
        } finally {
            setRemovedListingBusyAction(null);
            setMovingToCollection(false);
            setUpdatingId(null);
        }
    };

    const confirmMoveToCollection = async () => {
        if (!pendingMoveToCollection) return;
        await moveRemovedListingToCollection(pendingMoveToCollection);
    };

    const handleRemovedListingRelist = async () => {
        if (!removedListingOptions) return;
        setRemovedListingBusyAction('relist');
        try {
            const result = await updateStatus(removedListingOptions.marketplaceItemId, 'Active');
            if (result) {
                const nextStatus = result?.listingStatus || 'Active';
                success(
                    nextStatus === 'UnderReview'
                        ? 'Listing submitted for admin approval.'
                        : 'Listing relisted for 30 days.'
                );
                setRemovedListingOptions(null);
            }
        } finally {
            setRemovedListingBusyAction(null);
        }
    };

    const handleRemovedListingMoveToCollection = async () => {
        if (!removedListingOptions) return;
        setRemovedListingBusyAction('collection');
        try {
            await moveRemovedListingToCollection(removedListingOptions);
        } finally {
            setRemovedListingBusyAction(null);
        }
    };

    const confirmRelistListing = async () => {
        if (!pendingRelist) return;
        setRelistingListing(true);
        try {
            const result = await updateStatus(pendingRelist.marketplaceItemId, 'Active');
            if (result) {
                setPendingRelist(null);
                const nextStatus = result?.listingStatus || 'Active';
                success(
                    nextStatus === 'UnderReview'
                        ? 'Listing submitted for admin approval.'
                        : 'Listing relisted for 30 days.'
                );
            }
        } finally {
            setRelistingListing(false);
        }
    };

    const confirmPublishListing = async () => {
        if (!pendingPublish) return;
        setPublishingListing(true);
        try {
            const ok = await updateStatus(pendingPublish.marketplaceItemId, 'UnderReview');
            if (ok) {
                setPendingPublish(null);
                success('Listing submitted for admin approval.');
            }
        } finally {
            setPublishingListing(false);
        }
    };

    const confirmMarkSold = async (saleDetails) => {
        if (!pendingMarkSold) return;
        setMarkingSold(true);
        try {
            const ok = await updateStatus(pendingMarkSold.marketplaceItemId, 'Sold', false, saleDetails);
            if (ok) {
                if (saleDetails?.soldOnPlatform) {
                    success('Marked as sold. Item moved to the buyer’s collection.');
                } else {
                    success('Marked as sold. Item removed from your collection.');
                }
                setPendingMarkSold(null);
            }
        } finally {
            setMarkingSold(false);
        }
    };

    if (!authReady || !isAuthenticated) return null;

    return (
        <div className="mp-page my-mp-listings-page">
            <aside className="mp-sidebar">
                <BrowseSidebarSticky>
                    <p className="msv-page-eyebrow">Buy &amp; sell</p>
                    <h1>My Listings</h1>
                    <button type="button" className="mp-sell-btn" onClick={() => navigate('/createmarketplaceitem')}>
                        <FiPlus />
                        Create listing
                    </button>
                    <button
                        type="button"
                        className="mp-sell-from-collection-btn"
                        onClick={() => navigate('/createmarketplaceitem', { state: { pickFromCollection: true } })}
                    >
                        Sell from my collection
                    </button>
                    <button
                        type="button"
                        className="mp-sell-from-collection-btn"
                        onClick={() => navigate('/marketplace')}
                    >
                        <FiShoppingBag aria-hidden />
                        Browse marketplace
                    </button>
                    <button
                        type="button"
                        className="mp-sell-from-collection-btn"
                        onClick={() => navigate('/myHeroCardWishList?type=marketplace')}
                    >
                        <FiHeart aria-hidden />
                        Wishlist
                    </button>

                    <p className="mp-sidebar-label">Manage</p>
                    <nav className="mp-cat-list">
                        <button type="button" className="mp-cat-item active">
                            <FiList aria-hidden />
                            All my listings
                        </button>
                    </nav>

                    <label className="my-mp-expired-toggle">
                        <input
                            type="checkbox"
                            checked={showExpired}
                            onChange={(e) => setShowExpired(e.target.checked)}
                        />
                        Show expired &amp; sold
                    </label>
                </BrowseSidebarSticky>
            </aside>

            <div className="mp-main">
                <div className="mp-sticky-head">
                    {bannerMessage && (
                        <div className="my-mp-banner" role="status">
                            {bannerMessage}
                        </div>
                    )}

                    <div className="mp-heading-row">
                        <div className="mp-heading-block">
                            <p className="msv-page-eyebrow">Seller garage</p>
                            <h2>My Listings</h2>
                        </div>
                        <span className="mp-listing-count">
                            {loading
                                ? 'Loading…'
                                : `${listings.length} listing${listings.length === 1 ? '' : 's'}`}
                        </span>
                    </div>

                    <div className="my-mp-mobile-toolbar">
                        <label className="my-mp-expired-toggle">
                            <input
                                type="checkbox"
                                checked={showExpired}
                                onChange={(e) => setShowExpired(e.target.checked)}
                            />
                            Show expired &amp; sold
                        </label>
                    </div>
                </div>

                <div className="mp-grid">
                    {loading &&
                        Array.from({ length: 8 }).map((_, i) => (
                            <div key={`sk-${i}`} className="mp-listing-card mp-skeleton">
                                <div className="mp-card-image" />
                                <div className="mp-card-body">
                                    <div className="mp-skel-line mp-skel-price" />
                                    <div className="mp-skel-line" />
                                    <div className="mp-skel-line mp-skel-short" />
                                </div>
                            </div>
                        ))}

                    {!loading &&
                        listings.map((item) => {
                            const status = item.listingStatus || 'Active';
                            const nowUnix = Math.floor(Date.now() / 1000);
                            const isExpired =
                                status === 'Expired' ||
                                (status === 'Active' &&
                                    item.renewalDueUnix > 0 &&
                                    item.renewalDueUnix <= nowUnix);
                            const showRenew = status === 'Active' || status === 'Expired';
                            const canRenew = isExpired;
                            const canMarkSold = status === 'Active' || status === 'Expired';
                            const canRemove = status === 'Active' || status === 'Expired';
                            const canRelist = status === 'Removed';
                            const canPublish = status === 'Rejected';
                            const canEdit =
                                status === 'Rejected' ||
                                status === 'Active' ||
                                status === 'Expired' ||
                                isExpired;
                            const canMoveToCollection =
                                status === 'Removed' || status === 'Rejected' || status === 'UnderReview';
                            const imageSrc = resolveImageSrc(item);
                            const place = [item.city, item.state].filter(Boolean).join(', ');
                            const busy = updatingId === item.marketplaceItemId;

                            return (
                                <div key={item.marketplaceItemId} className="mp-listing-card my-mp-card">
                                    <button
                                        type="button"
                                        className="my-mp-card-hit"
                                        onClick={() => navigate(`/marketplaceItem/${item.marketplaceItemId}`)}
                                    >
                                        <div className="mp-card-image">
                                            {imageSrc ? (
                                                <img src={imageSrc} alt="" loading="lazy" decoding="async" />
                                            ) : (
                                                <span>No photo</span>
                                            )}
                                            <span className={`my-mp-status status-${status.toLowerCase()}`}>
                                                {statusBadgeLabel(status)}
                                            </span>
                                        </div>
                                        <div className="mp-card-body">
                                            <p className="mp-card-price">{formatPrice(item.price)}</p>
                                            <p className="mp-card-title">{toTitleCase(item.title)}</p>
                                            <p className="mp-card-meta">
                                                {place && (
                                                    <span>
                                                        <FiMapPin /> {place}
                                                    </span>
                                                )}
                                                {item.dateTimeAddedUnix ? (
                                                    <span>{timeAgo(item.dateTimeAddedUnix)}</span>
                                                ) : null}
                                            </p>
                                            {(item.shippingAvailable || item.localPickupAvailable !== false) && (
                                                <div className="mp-card-fulfillment">
                                                    {item.shippingAvailable && (
                                                        <p className="mp-card-ship">
                                                            <FiTruck /> Ships
                                                        </p>
                                                    )}
                                                    {item.localPickupAvailable !== false && (
                                                        <p className="mp-card-ship">
                                                            <FiMapPin /> Pickup
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </button>

                                    {(showRenew || canMarkSold || canRemove || canRelist || canPublish || canEdit || canMoveToCollection) && (
                                        <div className="my-mp-card-actions">
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="my-mp-relist-btn"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        navigate(
                                                            `/marketplaceItem/${item.marketplaceItemId}?edit=1`
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </button>
                                            )}
                                            {showRenew && (
                                                <button
                                                    type="button"
                                                    disabled={busy || !canRenew}
                                                    title={
                                                        canRenew
                                                            ? undefined
                                                            : 'Renew becomes available after the 30-day listing period ends'
                                                    }
                                                    onClick={() => updateStatus(item.marketplaceItemId, 'Active', true)}
                                                >
                                                    Renew
                                                </button>
                                            )}
                                            {canMarkSold && (
                                                <button
                                                    type="button"
                                                    disabled={busy || markingSold}
                                                    onClick={() => setPendingMarkSold(item)}
                                                >
                                                    Mark sold
                                                </button>
                                            )}
                                            {canPublish && (
                                                <button
                                                    type="button"
                                                    className="my-mp-relist-btn"
                                                    disabled={busy}
                                                    onClick={() => setPendingPublish(item)}
                                                >
                                                    Resubmit
                                                </button>
                                            )}
                                            {canRelist && (
                                                <button
                                                    type="button"
                                                    className="my-mp-relist-btn"
                                                    disabled={busy}
                                                    onClick={() => setPendingRelist(item)}
                                                >
                                                    Relist
                                                </button>
                                            )}
                                            {canMoveToCollection && (
                                                <button
                                                    type="button"
                                                    className="my-mp-collection-btn"
                                                    disabled={busy}
                                                    onClick={() => setPendingMoveToCollection(item)}
                                                >
                                                    {status === 'UnderReview' ? 'Cancel listing' : 'Delete listing'}
                                                </button>
                                            )}
                                            {canRemove && (
                                                <button
                                                    type="button"
                                                    className="my-mp-delete-btn"
                                                    disabled={busy}
                                                    onClick={() => setPendingDelete(item)}
                                                >
                                                    Pause listing
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>

                {!loading && listings.length === 0 && (
                    <div className="mp-empty">
                        <div className="mp-empty-icon" aria-hidden>
                            <FiPackage />
                        </div>
                        <h3>No listings in your garage yet</h3>
                        <p>List a collectible to get it on the grid. Buyers can message you straight from the listing.</p>
                        <button type="button" className="mp-sell-btn" onClick={() => navigate('/createmarketplaceitem')}>
                            <FiPlus />
                            Create a listing
                        </button>
                    </div>
                )}
            </div>

            <div className="mp-mobile-tabbar" aria-label="Section navigation">
                <MarketplaceHubNav layout="chips" />
            </div>

            <ConfirmDialog
                open={!!pendingDelete}
                title="Pause listing?"
                message={
                    <>
                        This takes &quot;{toTitleCase(pendingDelete?.title || 'this listing')}&quot; down from the marketplace, but it will stay in{' '}
                        <strong>My Listings</strong> if you want to relist it later.
                    </>
                }
                confirmLabel="Pause listing"
                cancelLabel="Cancel"
                busyLabel="Pausing…"
                busy={deletingListing}
                onConfirm={confirmDeleteListing}
                onCancel={() => !deletingListing && setPendingDelete(null)}
            />

            <RemovedListingOptionsDialog
                open={!!removedListingOptions}
                listingTitle={toTitleCase(removedListingOptions?.title || 'this listing')}
                busyAction={removedListingBusyAction}
                onRelist={handleRemovedListingRelist}
                onMoveToCollection={handleRemovedListingMoveToCollection}
                onDismiss={() => !removedListingBusyAction && setRemovedListingOptions(null)}
            />

            <ConfirmDialog
                open={!!pendingRelist}
                variant="success"
                title="Relist this item?"
                message={`This puts "${toTitleCase(pendingRelist?.title || 'this listing')}" back on the marketplace for 30 days.`}
                confirmLabel="Relist for 30 days"
                cancelLabel="Cancel"
                busy={relistingListing}
                onConfirm={confirmRelistListing}
                onCancel={() => !relistingListing && setPendingRelist(null)}
            />

            <ConfirmDialog
                open={!!pendingPublish}
                variant="success"
                title="Resubmit this listing?"
                message={`"${toTitleCase(pendingPublish?.title || 'This listing')}" will go back to admin for approval. It will not appear on the marketplace until approved.`}
                confirmLabel="Resubmit for approval"
                cancelLabel="Cancel"
                busy={publishingListing}
                onConfirm={confirmPublishListing}
                onCancel={() => !publishingListing && setPendingPublish(null)}
            />

            <ConfirmDialog
                open={!!pendingMoveToCollection}
                title={
                    pendingMoveToCollection?.listingStatus === 'UnderReview'
                        ? 'Cancel listing?'
                        : 'Delete listing?'
                }
                message={
                    pendingMoveToCollection?.listingStatus === 'UnderReview'
                        ? `This cancels admin review for "${toTitleCase(pendingMoveToCollection?.title || 'this listing')}". The item stays in your collection.`
                        : `This deletes "${toTitleCase(pendingMoveToCollection?.title || 'this listing')}" from the marketplace. The item stays in your collection.`
                }
                confirmLabel={
                    pendingMoveToCollection?.listingStatus === 'UnderReview'
                        ? 'Cancel listing'
                        : 'Delete listing'
                }
                cancelLabel="Keep listing"
                busyLabel={
                    pendingMoveToCollection?.listingStatus === 'UnderReview'
                        ? 'Cancelling…'
                        : 'Deleting…'
                }
                busy={movingToCollection}
                onConfirm={confirmMoveToCollection}
                onCancel={() => !movingToCollection && setPendingMoveToCollection(null)}
            />

            <MarkAsSoldDialog
                open={!!pendingMarkSold}
                listingTitle={toTitleCase(pendingMarkSold?.title || '')}
                busy={markingSold}
                onConfirm={confirmMarkSold}
                onCancel={() => !markingSold && setPendingMarkSold(null)}
            />
        </div>
    );
};

export default MyMarketplaceListings;
